import { useEffect, useState } from "react";
import cookie from "react-cookies";
import { getAllAcMenus } from "../api/tenant";
import { RIMS_COOKIE_STG_CONSTANTS, RIMS_LOCAL_STG_CONSTANTS } from "../constants";
import { useConfigStore } from "../store/config";
import type { AcMenuItem } from "../types/acMenu";
import { fetchLayeredMenuPrivilege } from "../acMenu/bootstrapMenuPrivilege";
import { filterMatchedAcMenus } from "../acMenu/filterAcMenus";

/**
 * App bootstrap: wait for menuPrivilege + getAllAcMenus, then ∩ into matchedAcMenus.
 * Must complete before createAppRouter / RouterProvider (deep-link safety).
 */
export function useBootstrapAcMenus(): { ready: boolean } {
  const [ready, setReady] = useState(false);
  const updateMenuPrivilege = useConfigStore((s) => s.updateMenuPrivilege);
  const updateMatchedAcMenus = useConfigStore((s) => s.updateMatchedAcMenus);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const userId = cookie.load(RIMS_COOKIE_STG_CONSTANTS.USER_ID);

      let menuPrivilege: string[] = [];
      let menus: AcMenuItem[] = [];

      try {
        if (userId) {
          const [privNames, acRes] = await Promise.all([
            fetchLayeredMenuPrivilege(String(userId)),
            getAllAcMenus().catch(() => ({ data: [] as AcMenuItem[] })),
          ]);
          menuPrivilege = privNames;
          menus = Array.isArray(acRes?.data) ? acRes.data : [];
        } else {
          // Still settle so App can mount (Layout will handle missing user)
          const acRes = await getAllAcMenus().catch(() => ({
            data: [] as AcMenuItem[],
          }));
          menus = Array.isArray(acRes?.data) ? acRes.data : [];
        }
      } catch {
        menuPrivilege = [];
        menus = [];
      }

      if (cancelled) return;

      updateMenuPrivilege(menuPrivilege);
      window.localStorage.setItem(
        RIMS_LOCAL_STG_CONSTANTS.MENU_AUTH_LIST,
        JSON.stringify(menuPrivilege)
      );
      const matched = filterMatchedAcMenus(menus, menuPrivilege);
      updateMatchedAcMenus(matched);
      setReady(true);
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [updateMenuPrivilege, updateMatchedAcMenus]);

  return { ready };
}
